import escapeHtml from 'escape-html';
import { omit } from 'radash';

import { AppName, CompanyProfile, CompanyService, MembershipCatalog, ProfileQuery } from 'models';
import { CryptoHelpers } from 'helpers/CryptoHelpers';
import { TipTapHelpers } from 'helpers/TipTapHelpers';

import { EditType } from 'components/company-profile-edit/edit-settings';

const CompanyHelpers = {
    swr: {
        profile: (profileId: string) => `/api/profile/${profileId}`,
        profiles: (profileIds: string[]) => `/api/profiles/${[...profileIds].sort().join(',')}`,
        count: (companyId: string) => `/companies/${companyId}/count`,
        accessRequests: (userId: string, status: 'pending' | 'approved' | 'rejected', companyId?: string) =>
            companyId
                ? `/api/company-access-requests/${userId}?company=${companyId}&status=${status}`
                : `/api/company-access-requests/${userId}?status=${status}`,
    },

    urls: {
        create: (options?: { id?: string; redirect?: string; step?: number; partner?: string }) => {
            const { id, redirect, step, partner } = options || {};

            const query = [];

            if (redirect) {
                query.push(`redirect=${encodeURIComponent(redirect)}`);
            }

            if (step) {
                query.push(`step=${step}`);
            }

            if (partner) {
                query.push(`partner=${partner}`);
            }

            let baseUrl = '/profiles/signup';

            if (id) {
                baseUrl += `/${id}`;
            }

            return `${baseUrl}${query.length ? `?${query.join('&')}` : ''}`;
        },
        view: (slug: string, hash?: string) => `/profiles/${slug}${hash ? `#${hash}` : ''}`,
        article: (slug: string, id: string) => `/profiles/${slug}/articles/${id}`,
        collateral: (slug: string) => `/profiles/${slug}/collateral`,
        collateralItem: (slug: string, id: string) => `/profiles/${slug}/collateral/${id}`,

        edit: (slug: string, type: EditType = 'general') =>
            type === 'general' ? `/profiles/${slug}/edit` : `/profiles/${slug}/edit/${type}`,

        qr: (slug: string) => `/profiles/${slug}/edit/qr`,
        showtimeLeads: (slug: string) => `/profiles/${slug}/edit/leads`,
        requestAccess: (slug: string, options?: { preview?: string; redirect?: string }) => {
            const { preview, redirect } = options || {};

            const query = new URLSearchParams();

            if (preview) {
                query.append('preview', preview);
            }

            if (redirect) {
                query.append('redirect', redirect);
            }

            const queryAsString = query.toString();

            return `/profiles/${slug}/request-access${queryAsString ? `?${queryAsString}` : ''}`;
        },
        claim: (catalog: MembershipCatalog) => `/claim-your-profile?catalog=${catalog}`,
        search: () => '/profiles',
        overview: () => '/profiles',
        manage: () => '/profiles/manage',
        dcManufacturers: () => '/dcproducts/manufacturers',
        categories: (subcategory: string, filters?: ProfileQuery) =>
            `/profiles/categories/${subcategory}${filters ? `?filters=${JSON.stringify(filters)}` : ''}`,
    },

    claimInviteEmail: ({
        companyName,
        inviterName,
        inviterEmail,
        appName = AppName.DCIDE,
    }: {
        companyName: string;
        inviterName: string;
        inviterEmail: string;
        appName?: AppName;
    }) => ({
        subject: `${inviterName} invited you to claim the ${companyName} profile`,
        title: `<strong>${escapeHtml(inviterName)}</strong> invited you to claim the <strong>${escapeHtml(companyName)}</strong> profile`,
        text:
            `${escapeHtml(inviterName)} (${escapeHtml(inviterEmail)}) thinks you are the right person to manage the ` +
            `<strong>${escapeHtml(companyName)}</strong> company profile on ${appName}. ` +
            'Claiming it lets you keep your company and product information accurate, showcase your products to system designers, and receive leads.',
        buttonLabel: `Claim the ${companyName} profile`,
    }),

    isDistributor: (company?: CompanyProfile | null) => {
        return company?.services.includes(CompanyService.DISTRIBUTION) || false;
    },

    modals: {
        choosePartner: 'companyProfileChoosePartner',
        createPartner: 'companyProfileCreatePartner',
        requestRemoval: 'companyProfileRequestRemoval',
    },

    generatePreviewUrl: (company: Pick<CompanyProfile, 'slug' | 'internal'>) => {
        let shareUrl = window.location.origin + CompanyHelpers.urls.view(company.slug);

        if (company.internal) {
            shareUrl += `?preview=${CryptoHelpers.unsafeSimpleHash(company.slug)}`;
        }

        return shareUrl;
    },

    generateClaimUrl: (company: Pick<CompanyProfile, 'slug'>) =>
        window.location.origin +
        CompanyHelpers.urls.requestAccess(company.slug, { preview: CryptoHelpers.unsafeSimpleHash(company.slug) }),

    generateJoinUrl: (company: Pick<CompanyProfile, 'slug'>) =>
        window.location.origin + CompanyHelpers.urls.requestAccess(company.slug),

    summarizeLocations: (locations: CompanyProfile['locations'], maxShown = 3): string => {
        if (!locations?.length) return '';

        const resolved = locations.map(({ address }) => ({
            label: address?.city || address?.state || '',
            country: address?.country || '',
        }));

        const valid = resolved.filter((l) => l.label || l.country);

        if (!valid.length) return '';

        const unique = Array.from(new Map(valid.map((l) => [`${l.label}-${l.country}`, l])).values());

        const uniqueCountries = Array.from(new Set(unique.map((l) => l.country).filter(Boolean)));

        // Case 1 – Multiple countries involved
        if (uniqueCountries.length > 1) {
            const shown = uniqueCountries.slice(0, maxShown).join(' + ');
            const remaining = uniqueCountries.length - maxShown;
            return remaining > 0 ? `${shown} + ${remaining} more` : shown;
        }

        // Case 2 – Single country, multiple labels (city/state mix)
        const country = uniqueCountries[0];
        const labels = Array.from(new Set(unique.map((l) => l.label).filter(Boolean)));

        if (!labels.length) return country;

        const shown = labels.slice(0, maxShown).join(' + ');
        const remaining = labels.length - maxShown;

        if (country) {
            return remaining > 0 ? `${shown}, ${country} + ${remaining} more` : `${shown}, ${country}`;
        }

        // Case 3 – No country info, just show labels
        return remaining > 0 ? `${shown} + ${remaining} more` : shown;
    },

    hasLogo: (images: CompanyProfile['logos']) =>
        typeof images.small === 'string' ||
        !!images.small?.url ||
        typeof images.large === 'string' ||
        !!images?.large?.url,

    hasCover: (cover: CompanyProfile['cover']) => typeof cover === 'string' || !!cover?.url,

    hasAbout: (about: CompanyProfile['about']) => TipTapHelpers.hasContent(about),

    hasASocial: (socials: CompanyProfile['socials']) => Object.values(socials ?? {}).some((social) => !!social),

    hasInAppSupport: (services: CompanyProfile['services']) => services.includes(CompanyService.IN_APP_SUPPORT),

    getProgress: (
        company: CompanyProfile,
        hasProducts: boolean = false,
        hasReferenceDesigns: boolean = false,
        hasCaseStudies: boolean = false,
    ): {
        key: string;
        progress: boolean;
    }[] => {
        const fieldsToPick: Array<keyof CompanyProfile> = [
            'about',
            'logos',
            'cover',
            'contactPeople',
            'locations',
            'socials',
            'promos',
        ];

        const getValueProgress = (key: keyof CompanyProfile, value: any) => {
            switch (key) {
                case 'about':
                    return CompanyHelpers.hasAbout(value as CompanyProfile['about']);
                case 'logos':
                    return CompanyHelpers.hasLogo(value as CompanyProfile['logos']);
                case 'cover':
                    return CompanyHelpers.hasCover(value as CompanyProfile['cover']);
                case 'socials':
                    return CompanyHelpers.hasASocial(value as CompanyProfile['socials']);
                default: {
                    if (Array.isArray(value)) {
                        return !!value.length;
                    }

                    return !!value;
                }
            }
        };

        const progress: {
            key: string;
            progress: boolean;
        }[] = fieldsToPick.map((key) => {
            const value = company[key];

            return {
                key,
                progress: getValueProgress(key, value),
            };
        });

        progress.unshift({ key: 'caseStudies', progress: hasCaseStudies });
        progress.unshift({ key: 'referenceDesigns', progress: hasReferenceDesigns });
        progress.unshift({ key: 'products', progress: hasProducts });

        return progress;
    },

    getAverageProgress: (
        progress: {
            key: string;
            progress: boolean;
        }[],
    ) => Math.round((progress.filter(({ progress }) => progress).length / progress.length) * 100),

    hasLocationInformation: (location: CompanyProfile['locations'][0]): boolean => {
        const { name, address, contactInformation } = location;

        const hasAddressFields = address && Object.values(omit(address, ['coordinates'])).some((field) => !!field);

        return !!(name || hasAddressFields || contactInformation?.email || contactInformation?.phone);
    },
};

export { CompanyHelpers };
